/**
 * Section management helpers for the case plan detail view: write-access rules, the
 * Active/Archived tab split, and the pure list transformations the section hook applies
 * optimistically before (and after) a mutation response arrives.
 *
 * Plan/section/widget API mapping lives in reentry-plan-detail.util.js.
 */

import {
  CASE_PLAN_SECTION_CONFIRMATION_COPY,
  CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS,
  CASE_PLAN_SECTION_FALLBACK_TITLE,
  CASE_PLAN_SECTION_MOVE_DIRECTIONS,
  CASE_PLAN_SECTION_PLACEHOLDER_TITLE,
  CASE_PLAN_SECTION_STATUSES,
  CASE_PLAN_SECTION_TABS,
  CASE_PLAN_STATUSES,
  DEFAULT_CASE_PLAN_SECTION_TAB,
} from '../constants/reentry-planning.constants';

// ========== Status predicates ==========

/**
 * Section status is tri-state, and the three predicates below are mutually exclusive: a
 * section is archived, deleted, or active, and never two of those at once.
 */

/** Only an explicit `archived` status lists a section under the Archived tab. */
const isArchivedSection = (section) => section?.status === CASE_PLAN_SECTION_STATUSES.ARCHIVED;

/**
 * Delete is terminal: the detail response stops listing such a section entirely, so it belongs
 * to no tab. The predicate is what keeps a deleted section off screen if one ever reaches the
 * list anyway — from the Delete response, or from a payload that leaks one.
 */
export const isDeletedSection = (section) => section?.status === CASE_PLAN_SECTION_STATUSES.DELETED;

/**
 * Everything that is neither archived nor deleted, including the absent status a payload
 * predating section statuses carries, so such a section still renders on Active.
 */
const isActiveSection = (section) => !isArchivedSection(section) && !isDeletedSection(section);

/**
 * Splits the section list for the Active/Archived tabs, preserving backend order. A deleted
 * section is on neither tab.
 */
export const filterSectionsForTab = (sections = [], tab) =>
  tab === CASE_PLAN_SECTION_STATUSES.ARCHIVED
    ? sections.filter(isArchivedSection)
    : sections.filter(isActiveSection);

/** Tab metadata (label and the ids tying the tab to its panel) for the rendered tab value. */
export const resolveSectionTabConfig = (tabValue) =>
  CASE_PLAN_SECTION_TABS.find((tab) => tab.value === tabValue) ?? DEFAULT_CASE_PLAN_SECTION_TAB;

// ========== Write access ==========

/**
 * Destructive footer action the plan status permits. Draft soft-deletes, Active and Released
 * archive, and every other status (Archived plan, template) offers neither.
 */
const PLAN_STATUS_DESTRUCTIVE_ACTION = {
  [CASE_PLAN_STATUSES.DRAFT]: CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.DELETE,
  [CASE_PLAN_STATUSES.ACTIVE]: CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.ARCHIVE,
  [CASE_PLAN_STATUSES.RELEASED]: CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.ARCHIVE,
};

const normalizePlanStatus = (planStatus) => String(planStatus ?? '').trim().toLowerCase();

/**
 * @param {string} planStatus - `plan.status` from the detail response.
 * @returns {'delete' | 'archive' | null} The single destructive action to render, or null.
 */
export const resolveSectionDestructiveAction = (planStatus) =>
  PLAN_STATUS_DESTRUCTIVE_ACTION[normalizePlanStatus(planStatus)] ?? null;

/**
 * Mirrors the backend section-write gate. Every clause must hold; UI gating hides the
 * controls but never replaces the server's 403.
 *
 * @param {{ hasEditPermission: boolean, plan: object | null }} params - `plan` carries
 *   `isEditor` (membership of *this* plan), `isWritablePlanType` (reentry, not preview),
 *   and `status`.
 * @returns {boolean} True when the staff member may mutate this plan's sections.
 */
export const canManageCasePlanSections = ({ hasEditPermission, plan } = {}) =>
  Boolean(hasEditPermission) &&
  plan?.isEditor === true &&
  plan?.isWritablePlanType === true &&
  resolveSectionDestructiveAction(plan?.status) !== null;

// ========== Section identity ==========

/**
 * Stable identity of a rendered section. A section created optimistically is rendered under
 * its `clientKey` before the server answers and keeps that key afterwards, so replacing its
 * temporary id with the real UUID never remounts the row the admin is typing in.
 */
export const getSectionKey = (section) => section?.clientKey ?? section?.id ?? null;

/** True while a section exists only locally, waiting for its create to answer. */
export const isPendingSection = (section) => section?.isPending === true;

/**
 * Placeholder row Add Section inserts synchronously. It mirrors exactly what
 * `POST /sections` persists, so settling the create changes nothing on screen. Its `id` is
 * the `clientKey` so field handlers can address it before a real id exists — the pending
 * registry is what keeps that temporary id from ever reaching the API.
 *
 * @param {string} clientKey - Locally generated key identifying this pending section.
 */
export const buildPendingSection = (clientKey) => ({
  id: clientKey,
  clientKey,
  title: CASE_PLAN_SECTION_PLACEHOLDER_TITLE,
  description: '',
  visibleToLearner: false,
  status: CASE_PLAN_SECTION_STATUSES.ACTIVE,
  sortOrder: 0,
  widgets: [],
  isPending: true,
});

/**
 * Drops sections that have no server id yet. Anything handed to an API taking section ids —
 * the export selection above all — must go through this first.
 */
export const excludePendingSections = (sections = []) =>
  sections.filter((section) => !isPendingSection(section));

// ========== List transformations ==========

const findSectionIndex = (sections, sectionId) =>
  sections.findIndex((section) => section.id === sectionId);

export const findSectionIndexByKey = (sections = [], sectionKey) =>
  sections.findIndex((section) => getSectionKey(section) === sectionKey);

export const findSectionById = (sections = [], sectionId) =>
  sections.find((section) => section.id === sectionId) ?? null;

export const findSectionByKey = (sections = [], sectionKey) =>
  sections.find((section) => getSectionKey(section) === sectionKey) ?? null;

/**
 * Inserts a section above `insertBeforeSectionKey`, or appends it when the anchor is null
 * (the trailing Add Section control and the empty plan) or no longer in the list. The anchor
 * is a section *key*, so a new section can be inserted above one that is itself still pending.
 */
export const insertSectionBefore = (sections = [], section, insertBeforeSectionKey) => {
  const anchorIndex = insertBeforeSectionKey
    ? findSectionIndexByKey(sections, insertBeforeSectionKey)
    : -1;

  if (anchorIndex === -1) {
    return [...sections, section];
  }

  return [...sections.slice(0, anchorIndex), section, ...sections.slice(anchorIndex)];
};

/**
 * Real anchor id the create request must send for a pending section: the first active section
 * below it that already exists on the server. Null appends, which is also the right answer
 * when nothing below it is persisted yet.
 *
 * Resolving this at request time (rather than at click time) is what keeps rapid Add Section
 * clicks in the right order — the row below may still have been pending when it was clicked.
 */
export const resolveInsertBeforeSectionId = (sections = [], clientKey) => {
  const pendingIndex = findSectionIndexByKey(sections, clientKey);

  if (pendingIndex === -1) {
    return null;
  }

  const anchor = sections
    .slice(pendingIndex + 1)
    .find((section) => !isPendingSection(section) && isActiveSection(section));

  return anchor?.id ?? null;
};

/**
 * Carries over the fields a mutation response cannot supply in renderable form: the
 * `clientKey` that keeps the React key stable, and the widgets already mapped for display
 * (no section mutation changes them, and the raw response shape is not renderable).
 */
export const preserveSectionDisplayFields = (section, previous) => ({
  ...section,
  ...(previous?.clientKey ? { clientKey: previous.clientKey } : {}),
  widgets: previous?.widgets ?? section?.widgets ?? [],
});

/** Applies a mutation response to the section it belongs to, in place. */
export const mergeSectionResponse = (sections = [], section) => {
  if (!section?.id) {
    return sections;
  }

  return sections.map((existing) =>
    existing.id === section.id ? preserveSectionDisplayFields(section, existing) : existing,
  );
};

/**
 * Replaces a pending placeholder with the created section. `localFields` are the edits made
 * while the create was in flight; they win over the persisted placeholder values so no
 * keystroke is lost, and `clientKey` survives so the React key never changes.
 */
export const settlePendingSection = (sections = [], clientKey, createdSection, localFields = {}) =>
  sections.map((section) =>
    getSectionKey(section) === clientKey
      ? { ...preserveSectionDisplayFields(createdSection, section), clientKey, ...localFields }
      : section,
  );

/** Applies patched fields to one section without touching its widgets or order. */
export const applySectionFields = (sections = [], sectionId, fields) =>
  sections.map((section) => (section.id === sectionId ? { ...section, ...fields } : section));

/**
 * Current values of the fields a patch is about to change, so a rejected patch reverts
 * exactly those fields instead of restoring a whole list snapshot that would erase unrelated
 * changes made while the request was in flight.
 */
export const pickSectionFields = (section, fields = {}) =>
  Object.keys(fields).reduce(
    (picked, field) => ({ ...picked, [field]: section?.[field] ?? null }),
    {},
  );

/** UI field names of a section mapped to the POST body the API accepts. */
const SECTION_API_FIELD_NAMES = {
  title: 'name',
  description: 'description',
  visibleToLearner: 'visibleToLearner',
};

/**
 * Translates edited UI fields into the section POST body. Unknown fields are dropped rather
 * than forwarded, because the API rejects a body it does not recognize.
 */
export const toSectionApiFields = (uiFields = {}) =>
  Object.entries(uiFields).reduce((apiFields, [field, value]) => {
    const apiField = SECTION_API_FIELD_NAMES[field];
    return apiField ? { ...apiFields, [apiField]: value } : apiFields;
  }, {});

export const removeSectionByKey = (sections = [], sectionKey) =>
  sections.filter((section) => getSectionKey(section) !== sectionKey);

const clampSectionIndex = (index, length) => Math.min(Math.max(index, 0), length);

/**
 * Puts a section back at the position it was removed from. The index is clamped because the
 * list may have shrunk in the meantime, and an already-present section is left alone so a
 * late restore cannot duplicate a row.
 */
export const reinsertSectionAt = (sections = [], section, index) => {
  const sectionKey = getSectionKey(section);

  if (sectionKey === null || findSectionIndexByKey(sections, sectionKey) !== -1) {
    return sections;
  }

  const targetIndex = clampSectionIndex(index, sections.length);

  return [...sections.slice(0, targetIndex), section, ...sections.slice(targetIndex)];
};

/**
 * Applies a mutation response to a section that may no longer be in the list: merged in place
 * when it is still there, reinserted at `index` when it is not. Archive removes the row from
 * the UI before sending the request, so the archived section that comes back lands at its old
 * position — absent from Active, listed under Archived, on `status` alone. Delete never comes
 * back through here: the row stays removed.
 */
export const mergeSectionInto = (sections = [], section, index) =>
  findSectionIndexByKey(sections, getSectionKey(section)) === -1
    ? reinsertSectionAt(sections, section, index)
    : mergeSectionResponse(sections, section);

const swapEntries = (sections, firstIndex, secondIndex) => {
  const swapped = [...sections];
  [swapped[firstIndex], swapped[secondIndex]] = [swapped[secondIndex], swapped[firstIndex]];
  return swapped;
};

/**
 * A section a swap may target: active, and already persisted. A pending row holds no position
 * on the server, so the move endpoint swaps straight past it — the local list must do the same
 * or the two orders drift apart.
 */
const isMovableSection = (section) => isActiveSection(section) && !isPendingSection(section);

const findAdjacentMovableIndex = (sections, fromIndex, step) => {
  for (let index = fromIndex + step; index >= 0 && index < sections.length; index += step) {
    if (isMovableSection(sections[index])) {
      return index;
    }
  }

  return -1;
};

/**
 * Swaps a section with its nearest movable sibling in `direction` — the same neighbour the
 * move endpoint picks, since archived, deleted, and still-pending rows are skipped. Returns
 * the input array unchanged at the first/last boundary so callers can treat a no-op as
 * "nothing to persist".
 */
export const moveSectionInDirection = (sections = [], sectionId, direction) => {
  const sectionIndex = findSectionIndex(sections, sectionId);

  if (sectionIndex === -1) {
    return sections;
  }

  const step = direction === CASE_PLAN_SECTION_MOVE_DIRECTIONS.UP ? -1 : 1;
  const siblingIndex = findAdjacentMovableIndex(sections, sectionIndex, step);

  return siblingIndex === -1 ? sections : swapEntries(sections, sectionIndex, siblingIndex);
};

/**
 * Target `sortOrder` for the move endpoint: the section's index among active persisted
 * siblings — the same list the server compacts to `0..n-1`.
 *
 * @param {object[]} sections - Section list after the optimistic reorder.
 * @param {string} sectionId - Persisted section id being moved.
 * @returns {number | null} Target slot, or null when the section is not movable.
 */
export const resolveSectionMoveSortOrder = (sections = [], sectionId) => {
  const activeIndex = sections
    .filter(isMovableSection)
    .findIndex((section) => section.id === sectionId);

  return activeIndex === -1 ? null : activeIndex;
};

const OPPOSITE_SECTION_MOVE_DIRECTION = {
  [CASE_PLAN_SECTION_MOVE_DIRECTIONS.UP]: CASE_PLAN_SECTION_MOVE_DIRECTIONS.DOWN,
  [CASE_PLAN_SECTION_MOVE_DIRECTIONS.DOWN]: CASE_PLAN_SECTION_MOVE_DIRECTIONS.UP,
};

/**
 * Undoes a rejected reorder by swapping the section back. Only the two swapped rows are
 * touched, so an edit made elsewhere while the move was in flight survives the rollback.
 */
export const revertSectionMove = (sections = [], sectionId, direction) =>
  moveSectionInDirection(sections, sectionId, OPPOSITE_SECTION_MOVE_DIRECTION[direction]);

const NO_SECTION_MOVE_ALLOWED = Object.freeze({ moveUpDisabled: true, moveDownDisabled: true });

/** A move is blocked when there is no neighbour that way, or the neighbour is still pending. */
const isSectionMoveBlocked = (visibleSections, index, step) => {
  const neighbour = visibleSections[index + step];

  return !neighbour || isPendingSection(neighbour);
};

/**
 * Which move arrows a section may offer among the sections rendered on its tab.
 *
 * A pending row cannot move at all, and neither can the row next to one: the server would swap
 * with the neighbour's persisted position, which a pending section does not have yet, so the
 * resulting order would be ambiguous. Both become available again once the create answers.
 */
export const resolveSectionMoveBoundaries = (visibleSections = [], sectionKey) => {
  const index = findSectionIndexByKey(visibleSections, sectionKey);

  if (index === -1 || isPendingSection(visibleSections[index])) {
    return NO_SECTION_MOVE_ALLOWED;
  }

  return {
    moveUpDisabled: isSectionMoveBlocked(visibleSections, index, -1),
    moveDownDisabled: isSectionMoveBlocked(visibleSections, index, 1),
  };
};

/**
 * Footer action props for the shared section component. Delete and Archive are mutually
 * exclusive, so exactly one handler — or neither — is ever supplied.
 *
 * @param {'delete' | 'archive' | null} destructiveAction - Action the plan status permits.
 * @param {Function} onRemove - Opens the confirmation dialog.
 * @returns {{ onDelete?: Function, onArchive?: Function }}
 */
export const buildSectionFooterActionProps = (destructiveAction, onRemove) => {
  if (!onRemove || destructiveAction === null) {
    return {};
  }

  return destructiveAction === CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.DELETE
    ? { onDelete: onRemove }
    : { onArchive: onRemove };
};

// ========== Confirmation copy ==========

const resolveSectionTitle = (section) => {
  const title = String(section?.title ?? '').trim();
  return title === '' ? CASE_PLAN_SECTION_FALLBACK_TITLE : title;
};

/**
 * Builds the Delete/Archive confirmation copy for a section.
 *
 * @param {object} section - UI section being removed.
 * @param {'delete' | 'archive'} action - Destructive action the plan status permits.
 * @returns {{ title: string, description: string, modalStatus: 'danger' | 'info' } | null}
 *   Null for an unsupported action. `modalStatus` is the confirmation modal treatment:
 *   Delete is destructive, Archive is not.
 */
export const buildSectionConfirmationCopy = (section, action) => {
  const copy = CASE_PLAN_SECTION_CONFIRMATION_COPY[action];

  if (!copy) {
    return null;
  }

  return {
    title: copy.buildTitle(resolveSectionTitle(section)),
    description: copy.description,
    modalStatus: copy.modalStatus,
  };
};
